import { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  makeStyles,
  Typography,
  Box,
  Divider,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import WarningIcon from '@material-ui/icons/Warning';
import { LinkAutocomplete } from '../LinkAutocomplete';
import { AutocompleteResult, CreateLinkInput, HomepageLink } from '../../types';

const useStyles = makeStyles(theme => ({
  field: {
    marginBottom: theme.spacing(2),
  },
  groupSection: {
    marginTop: theme.spacing(2),
  },
  groupLabel: {
    marginBottom: theme.spacing(1),
  },
  error: {
    color: theme.palette.error.main,
    marginTop: theme.spacing(1),
  },
  deleteSection: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.error.light + '20',
    borderRadius: theme.shape.borderRadius,
  },
  deleteWarning: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    color: theme.palette.error.main,
    marginBottom: theme.spacing(1),
  },
  deleteButton: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.error.dark,
    },
  },
  confirmDialog: {
    '& .MuiDialogContent-root': {
      paddingTop: theme.spacing(2),
    },
  },
  confirmWarning: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    color: theme.palette.error.main,
    marginBottom: theme.spacing(2),
  },
}));

interface LinkContributionDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: CreateLinkInput) => Promise<void>;
  linkType: 'onboarding' | 'team';
  userGroups: string[];
  userRef?: string; // User's entity ref for personal links
  initialUrl?: string; // Pre-populated URL (e.g., from drag and drop)
  initialTitle?: string; // Pre-populated title (e.g., from drag and drop)
  preSelectMyself?: boolean; // Pre-select "Myself" checkbox (e.g., from drag and drop)
  editingLink?: HomepageLink | null; // Link being edited (enables edit mode)
  onDelete?: (linkId: string) => Promise<void>; // Delete handler for edit mode
}

export const LinkContributionDialog = ({
  open,
  onClose,
  onSubmit,
  linkType,
  userGroups,
  userRef,
  initialUrl,
  initialTitle,
  preSelectMyself,
  editingLink,
  onDelete,
}: LinkContributionDialogProps) => {
  const classes = useStyles();
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const isEditMode = Boolean(editingLink);

  // Set initial values when dialog opens
  useEffect(() => {
    if (open) {
      if (editingLink) {
        // Edit mode: pre-fill from the link being edited
        setUrl(editingLink.url);
        setTitle(editingLink.title);
        setDescription(editingLink.description || '');
        setSelectedGroups(editingLink.groups);
      } else {
        // Add mode: use initial values (e.g., from drag and drop)
        if (initialUrl) {
          setUrl(initialUrl);
        }
        if (initialTitle) {
          setTitle(initialTitle);
        }
        if (preSelectMyself && userRef) {
          setSelectedGroups([userRef]);
        }
      }
    }
  }, [open, editingLink, initialUrl, initialTitle, preSelectMyself, userRef]);

  const resetForm = useCallback(() => {
    setUrl('');
    setTitle('');
    setDescription('');
    setSelectedGroups([]);
    setError(null);
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  const handleUrlChange = useCallback(
    (newUrl: string, result?: AutocompleteResult) => {
      setUrl(newUrl);
      if (result) {
        // Auto-fill title and description from existing link
        setTitle(result.title);
        if (result.description) {
          setDescription(result.description);
        }
      }
    },
    [],
  );

  const handleGroupToggle = useCallback((groupRef: string) => {
    setSelectedGroups(prev =>
      prev.includes(groupRef)
        ? prev.filter(g => g !== groupRef)
        : [...prev, groupRef],
    );
  }, []);

  const handleSubmit = useCallback(async () => {
    // Validation
    if (!url.trim()) {
      setError('URL is required');
      return;
    }
    if (!title.trim()) {
      setError('Title is required');
      return;
    }
    if (selectedGroups.length === 0) {
      setError('Select at least one option (Myself or a group)');
      return;
    }

    // Basic URL validation
    try {
      new URL(url);
    } catch {
      setError('Please enter a valid URL');
      return;
    }

    setError(null);
    setSubmitting(true);

    try {
      await onSubmit({
        url: url.trim(),
        title: title.trim(),
        description: description.trim() || undefined,
        linkType,
        groupRefs: selectedGroups,
      });
      handleClose();
    } catch (err: any) {
      setError(err.message || 'Failed to create link');
    } finally {
      setSubmitting(false);
    }
  }, [url, title, description, selectedGroups, linkType, onSubmit, handleClose]);

  const handleDelete = useCallback(async () => {
    if (!editingLink || !onDelete) return;

    setDeleting(true);
    try {
      await onDelete(editingLink.id);
      setConfirmDeleteOpen(false);
      handleClose();
    } catch (err: any) {
      setError(err.message || 'Failed to delete link');
    } finally {
      setDeleting(false);
    }
  }, [editingLink, onDelete, handleClose]);

  const formatGroupName = (groupRef: string) => {
    // Convert "group:default/engineering" to "Engineering"
    const parts = groupRef.split('/');
    const name = parts[parts.length - 1];
    return name.charAt(0).toUpperCase() + name.slice(1).replace(/-/g, ' ');
  };

  return (
    <>
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEditMode ? 'Edit' : 'Add'} {linkType === 'onboarding' ? 'Onboarding' : 'Team'} Link
      </DialogTitle>
      <DialogContent>
        <div className={classes.field}>
          <LinkAutocomplete
            value={url}
            onChange={handleUrlChange}
            label="URL"
            placeholder="Enter URL or search existing links"
            error={Boolean(error && !url)}
          />
        </div>

        <TextField
          className={classes.field}
          label="Title"
          value={title}
          onChange={e => setTitle(e.target.value)}
          variant="outlined"
          fullWidth
          required
          inputProps={{ maxLength: 200 }}
          helperText={`${title.length}/200`}
        />

        <TextField
          className={classes.field}
          label="Description (optional)"
          value={description}
          onChange={e => setDescription(e.target.value)}
          variant="outlined"
          fullWidth
          multiline
          rows={2}
          inputProps={{ maxLength: 1000 }}
        />

        <FormControl component="fieldset" className={classes.groupSection}>
          <FormLabel component="legend" className={classes.groupLabel}>
            Add for *
          </FormLabel>
          <FormGroup>
            {userRef && (
              <FormControlLabel
                key="myself"
                control={
                  <Checkbox
                    checked={selectedGroups.includes(userRef)}
                    onChange={() => handleGroupToggle(userRef)}
                    color="primary"
                  />
                }
                label="Myself (personal link)"
              />
            )}
            {userGroups.map(groupRef => (
              <FormControlLabel
                key={groupRef}
                control={
                  <Checkbox
                    checked={selectedGroups.includes(groupRef)}
                    onChange={() => handleGroupToggle(groupRef)}
                    color="primary"
                  />
                }
                label={formatGroupName(groupRef)}
              />
            ))}
          </FormGroup>
          {!userRef && userGroups.length === 0 && (
            <Typography variant="body2" color="textSecondary">
              You are not a member of any groups.
            </Typography>
          )}
        </FormControl>

        {isEditMode && onDelete && (
          <>
            <Divider style={{ marginTop: 24 }} />
            <Box className={classes.deleteSection}>
              <div className={classes.deleteWarning}>
                <WarningIcon fontSize="small" />
                <Typography variant="body2">
                  Permanently delete this link
                </Typography>
              </div>
              <Button
                variant="contained"
                className={classes.deleteButton}
                startIcon={<DeleteIcon />}
                onClick={() => setConfirmDeleteOpen(true)}
                disabled={deleting}
              >
                Delete Link
              </Button>
            </Box>
          </>
        )}

        {error && (
          <Typography className={classes.error} variant="body2">
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting || deleting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={submitting || deleting || (!userRef && userGroups.length === 0)}
        >
          {submitting ? (isEditMode ? 'Saving...' : 'Adding...') : (isEditMode ? 'Save' : 'Add Link')}
        </Button>
      </DialogActions>
    </Dialog>

    {/* Delete Confirmation Dialog */}
    <Dialog
      open={confirmDeleteOpen}
      onClose={() => setConfirmDeleteOpen(false)}
      className={classes.confirmDialog}
    >
      <DialogTitle>Delete Link?</DialogTitle>
      <DialogContent>
        <div className={classes.confirmWarning}>
          <WarningIcon />
          <Typography>This action cannot be undone.</Typography>
        </div>
        <Typography variant="body2" color="textSecondary">
          Are you sure you want to permanently delete "{editingLink?.title}"?
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setConfirmDeleteOpen(false)} disabled={deleting}>
          Cancel
        </Button>
        <Button
          onClick={handleDelete}
          className={classes.deleteButton}
          variant="contained"
          disabled={deleting}
        >
          {deleting ? 'Deleting...' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};
